<template>
  <div class="af">
    <CamposDisponiveis
      :nome="modalCamposNome"
      :campos="payloadCampos"
      :onSelect="onPayloadCampoSelect"
    />

    <div class="af-section__header">
      <h5 class="af-section__title">Ações executadas</h5>
      <p class="af-section__hint">Defina o que acontece quando este gatilho dispara. As ações executam na ordem em que aparecem.</p>
    </div>

    <div v-if="!acoesList.length" class="af-empty">
      <fa-icon icon="play-circle" class="af-empty__icon" />
      <p class="af-empty__title">Nenhuma ação configurada</p>
      <p class="af-empty__hint">Adicione pelo menos uma ação para que o gatilho tenha efeito.</p>
    </div>

    <draggable
      :list="acoesList"
      handle=".af-card__drag"
      animation="200"
      ghost-class="af-card--ghost"
      @end="onDragEnd"
    >
    <div v-for="(acao, idx) in acoesList" :key="acao.id || ('new-' + idx)" class="af-card">
      <div class="af-card__header">
        <div class="af-card__title">
          <button type="button" class="af-card__drag" title="Arrastar para reordenar">
            <fa-icon icon="grip-vertical" />
          </button>
          <span class="af-card__num">{{ idx + 1 }}</span>
          <div>
            <div class="af-card__label">Ação {{ idx + 1 }}</div>
            <div class="af-card__type">{{ tipoNome(acao.acao_tipo_slug) || 'Tipo não selecionado' }}</div>
          </div>
        </div>
        <div class="af-card__header-right">
          <div class="af-on-error">
            <span class="af-on-error__label">Em caso de falha:</span>
            <div class="af-on-error__group">
              <button
                type="button"
                class="af-on-error__btn"
                :class="{ 'is-active': (acao.on_error || 'continue') === 'continue' }"
                @click="setOnError(idx, 'continue')"
              >Continuar</button>
              <button
                type="button"
                class="af-on-error__btn"
                :class="{ 'is-active': acao.on_error === 'abort' }"
                @click="setOnError(idx, 'abort')"
              >Abortar</button>
            </div>
          </div>
          <button type="button" class="af-icon-btn af-icon-btn--danger" @click="remover(idx)" title="Remover ação">
            <fa-icon icon="trash-alt" />
          </button>
        </div>
      </div>

      <div class="af-card__body">
        <div class="form-group af-fg">
          <label class="af-label">Tipo de ação <span class="af-required">*</span></label>
          <select class="form-control af-input" :value="acao.acao_tipo_slug" @change="setTipo(idx, $event.target.value)">
            <option :value="null">— Selecione o tipo de ação —</option>
            <option v-for="tipo in tipos" :key="tipo.slug" :value="tipo.slug">{{ tipo.nome }}</option>
          </select>
        </div>

        <div v-if="acao.acao_tipo_slug" class="af-config">
          <div class="af-config__divider">
            <span>Configuração</span>
          </div>

          <!-- Bloco específico para 'notification': subtipo do aviso -->
          <div v-if="acao.acao_tipo_slug === 'notification'" class="form-group af-fg">
            <label class="af-label">Subtipo do aviso <span class="af-required">*</span></label>
            <select class="form-control af-input"
                    :value="getCfg(acao, 'tipo') || 'TIPO_SIMPLES'"
                    @change="setCfg(idx, 'tipo', $event.target.value)">
              <option v-for="t in tiposAviso" :key="t.slug" :value="t.slug">{{ t.label }}</option>
            </select>
            <p class="af-hint">Define a categoria visual/funcional do aviso interno.</p>
          </div>

          <!-- Notification: tipo_id (token p/ resolução server-side) -->
          <div v-if="acao.acao_tipo_slug === 'notification' && (getCfg(acao, 'tipo') || 'TIPO_SIMPLES') !== 'TIPO_SIMPLES'" class="form-group af-fg">
            <label class="af-label">ID do registro relacionado (token)</label>
            <input
              type="text"
              class="form-control af-input"
              :value="getCfg(acao, 'tipo_id')"
              @input="setCfg(idx, 'tipo_id', $event.target.value)"
              placeholder="Ex.: :pedidoId, :boletoId"
            />
            <p class="af-hint">Token resolvido do payload (use os tokens disponíveis abaixo).</p>
          </div>

          <!-- Notification: destinatários (toggle token vs manual) -->
          <div v-if="acao.acao_tipo_slug === 'notification'" class="form-group af-fg">
            <label class="af-label">Destinatários <span class="af-required">*</span></label>
            <div class="af-dest-toggle">
              <button type="button" class="af-on-error__btn"
                      :class="{ 'is-active': destMode(acao) === 'token' }"
                      @click="setDestMode(idx, 'token')"
                      :disabled="!colaboradorTokens.length"
                      :title="!colaboradorTokens.length ? 'Evento não expõe relação que resolva colaborador' : ''">
                Tomar do evento
              </button>
              <button type="button" class="af-on-error__btn"
                      :class="{ 'is-active': destMode(acao) === 'manual' }"
                      @click="setDestMode(idx, 'manual')">
                Escolher manualmente
              </button>
            </div>

            <div v-if="destMode(acao) === 'token'" style="margin-top:8px;">
              <div v-if="colaboradorTokenSelecionado(acao)" class="af-dest-token">
                <fa-icon icon="user" class="af-dest-token__icon" />
                <div class="af-dest-token__body">
                  <div class="af-dest-token__label">{{ colaboradorTokenSelecionado(acao).label }}</div>
                  <code class="af-dest-token__code">{{ colaboradorTokenSelecionado(acao).token }}</code>
                </div>
              </div>
              <p class="af-hint">O destinatário é o colaborador associado ao registro do evento — resolvido pelo backend no momento da execução.</p>
            </div>

            <div v-else style="margin-top:8px;">
              <EntidadeAutocomplete
                entidade="colaborador"
                :multiple="true"
                :value="getDestManual(acao)"
                @input="setDestManual(idx, $event)"
              />
            </div>
          </div>

          <div v-for="campo in schemaFor(acao.acao_tipo_slug)" :key="campo.name"
               v-show="!isHandledByCustomBlock(acao.acao_tipo_slug, campo.name) && !isFieldHiddenByDestMode(acao, campo.name)"
               class="form-group af-fg">
            <label class="af-label">
              {{ campo.label }}
              <span v-if="campo.required" class="af-required">*</span>
            </label>

            <input
              v-if="!campo.type || campo.type === 'text' || campo.type === 'number'"
              :type="campo.type === 'number' ? 'number' : 'text'"
              class="form-control af-input"
              :value="getCfg(acao, campo.name)"
              @input="setCfg(idx, campo.name, $event.target.value)"
              @focus="onFieldFocus($event)"
            />

            <textarea
              v-else-if="campo.type === 'textarea' || campo.type === 'json' || campo.type === 'sql' || campo.type === 'html'"
              class="form-control af-input"
              :class="{ 'af-input--code': ['json', 'sql'].includes(campo.type) }"
              rows="5"
              :value="getCfg(acao, campo.name)"
              @input="setCfg(idx, campo.name, $event.target.value)"
              @focus="onFieldFocus($event)"
            ></textarea>

            <select v-else-if="campo.type === 'select' && !campo.source" class="form-control af-input"
                    :value="getCfg(acao, campo.name)"
                    @change="setCfg(idx, campo.name, $event.target.value)">
              <option :value="null">—</option>
              <option v-for="opt in (campo.options || [])" :key="opt" :value="opt">{{ opt }}</option>
            </select>

            <select v-else-if="campo.type === 'select' && campo.source" class="form-control af-input"
                    :value="getCfg(acao, campo.name)"
                    @change="setCfg(idx, campo.name, $event.target.value)">
              <option :value="null">—</option>
              <option v-for="opt in (sourceOptions[campo.source] || [])"
                      :key="opt[campo.option_value || 'id']"
                      :value="opt[campo.option_value || 'id']">
                {{ opt[campo.option_label || 'nome'] }}
              </option>
            </select>

            <label v-else-if="campo.type === 'boolean'" class="af-checkbox">
              <input
                type="checkbox"
                :checked="!!getCfg(acao, campo.name)"
                @change="setCfg(idx, campo.name, $event.target.checked)"
              />
              <span class="af-checkbox__box">
                <fa-icon icon="check" />
              </span>
              <span class="af-checkbox__text">{{ campo.label_inline || 'Habilitado' }}</span>
            </label>

            <div v-else-if="campo.type === 'payload_path'" class="af-payload-input">
              <input
                type="text"
                class="form-control af-input"
                :value="getCfg(acao, campo.name)"
                @input="setCfg(idx, campo.name, $event.target.value)"
                placeholder="Ex: clienteId, cliente.id"
              />
              <button type="button" class="af-btn af-btn--ghost"
                      @click="abrirModalCampos(idx, campo.name)"
                      :disabled="!eventFields.length"
                      :title="eventFields.length ? 'Selecionar campo do payload' : 'Selecione um evento na aba Propriedades'">
                <fa-icon icon="search" /> Selecionar
              </button>
            </div>

            <div v-else-if="campo.type === 'email_recipients'" class="af-payload-input">
              <input
                type="text"
                class="form-control af-input"
                :value="getCfg(acao, campo.name)"
                @input="setCfg(idx, campo.name, $event.target.value)"
                @focus="onFieldFocus($event)"
                placeholder="email@dominio.com, :pedido.cliente.email"
              />
              <button type="button" class="af-btn af-btn--ghost"
                      @click="abrirModalCampos(idx, campo.name, 'email')"
                      :disabled="!eventFields.length"
                      :title="eventFields.length ? 'Adicionar e-mail do evento' : 'Selecione um evento na aba Propriedades'">
                <fa-icon icon="search" /> Inserir e-mail do evento
              </button>
            </div>

            <p v-if="campo.description" class="af-hint">{{ campo.description }}</p>

            <div v-if="shouldShowTokensFor(campo) && availableTokens.length" class="af-tokens">
              <span v-if="tokenKey(idx, campo.name) === primeiroTokenFieldKey" class="af-tokens__label">Inserir variável do evento:</span>
              <p v-if="tokenKey(idx, campo.name) === primeiroTokenFieldKey" class="af-tokens__desc">
                Variáveis são campos do evento (ex.: <code>:pedidoId</code>, <code>:pedido.cliente.nome</code>) que serão substituídos pelos valores reais quando o gatilho disparar. Clique em uma variável para inseri-la no campo focado acima.
              </p>
              <acao-objeto-campo-select
                :fields="availableTokensAsFields"
                :value="tokenBusca[tokenKey(idx, campo.name)] || ''"
                placeholder="Buscar token..."
                @input="onTokenPickerInput(idx, campo.name, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </draggable>

    <button type="button" class="af-btn af-btn--primary af-btn--block" @click="adicionar">
      <fa-icon icon="plus" /> Adicionar ação
    </button>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';
import draggable from 'vuedraggable';
import CamposDisponiveis from '@/views/CamposIntegracao/components/CamposDisponiveis.vue';
import EntidadeAutocomplete from '@/components/Filtros/EntidadeAutocomplete.vue';
import AcaoObjetoCampoSelect from './AcaoObjetoCampoSelect.vue';

// TODO(C9): trocar pela chamada ao endpoint `/admin/avisos/tipos`
// (espelha App\Constants\Aviso\TipoAviso). Mantido localmente até C9 expor a API.
const TIPOS_AVISO = [
  { slug: 'TIPO_SIMPLES',           label: 'Simples (genérico)' },
  { slug: 'TIPO_PEDIDO',            label: 'Pedido' },
  { slug: 'TIPO_ORCAMENTO',         label: 'Orçamento' },
  { slug: 'TIPO_BOLETO',            label: 'Boleto' },
  { slug: 'TIPO_NOTA_FISCAL',       label: 'Nota fiscal' },
  { slug: 'TIPO_CADASTRO_CLIENTE',  label: 'Cadastro de cliente' },
  { slug: 'TIPO_CHAT_CLIENTE',      label: 'Chat - Cliente' },
  { slug: 'TIPO_CHAT_COLABORADOR',  label: 'Chat - Colaborador' },
  { slug: 'TIPO_CHAT_PEDIDO',       label: 'Chat - Pedido' },
  { slug: 'TIPO_CHAT_ORCAMENTO',    label: 'Chat - Orçamento' },
  { slug: 'TIPO_CHAT_BOLETO',       label: 'Chat - Boleto' },
  { slug: 'TIPO_CHAT_NOTA_FISCAL',  label: 'Chat - Nota fiscal' },
  { slug: 'TIPO_CHAT_PRODUTO',      label: 'Chat - Produto' },
  { slug: 'TIPO_CHAT_GRUPO',        label: 'Chat - Grupo' },
];

// Campos que recebem tokens (insere/copia) — heurística por nome.
const TOKEN_TARGET_FIELD_NAMES = ['titulo', 'title', 'mensagem', 'message', 'corpo', 'body', 'link', 'url', 'subject', 'assunto'];

// Heurística para identificar campos do schema cujo "tipo lógico" é colaborador.
// Restrita ao colaborador "dono" do registro do evento (ex.: colaborador_id do
// pedido) — papéis adjacentes (vendedor, atendente, responsavel...) são
// excluídos porque não são o destinatário natural do evento.
//
// Comparação pelo último segmento do path (após o ponto) — o EventoCamposBuilder
// expande relações em campos como `pedido.colaborador_id`, então um match exato
// no nome completo falharia.
function fieldLooksLikeColaborador(f) {
  if (!f) return false;
  const tipo = String(f.tipo || f.type || '').toLowerCase();
  if (tipo === 'colaborador') return true;
  const fullName = String(f.name || '').toLowerCase();
  const tail = fullName.split('.').pop();
  return tail === 'colaborador_id' || tail === 'colaboradorid' || tail === 'colaborador';
}

export default {
  name: 'AcaoObjetoAcaoForm',
  components: { CamposDisponiveis, draggable, EntidadeAutocomplete, AcaoObjetoCampoSelect },
  props: {
    acoes:       { type: Array, default: null },
    tipos:       { type: Array, default: () => [] },
    eventFields: { type: Array, default: () => [] },
    eventFieldsSchema:    { type: Array, default: () => [] },
    eventRelationsSchema: { type: Array, default: () => [] },
    modalCamposNome: { type: String, default: 'acaoObjetoCamposPayload' },
  },
  data() {
    return {
      sourceOptions: {},
      activePathTarget: null,
      tiposAviso: TIPOS_AVISO,
      lastFocusedInput: null,
      // Termo de busca por (acaoIdx + nome do campo) — cada token-picker é
      // independente para que digitar em uma ação não afete as outras.
      tokenBusca: {},
    };
  },
  computed: {
    acoesList() {
      return Array.isArray(this.acoes) ? this.acoes : [];
    },
    payloadCampos() {
      const filtro = this.activePathTarget && this.activePathTarget.tipoFiltro;
      const map = {};
      (this.eventFields || []).forEach(f => {
        if (filtro === 'email' && !this.fieldLooksLikeEmail(f)) return;
        map[f.name] = f.label || f.name;
      });
      return map;
    },
    // Tokens disponíveis — combina fields_schema e relations_schema (se houver).
    availableTokens() {
      const out = [];
      const seen = new Set();
      const push = (token, label, grupo) => {
        if (!token || seen.has(token)) return;
        seen.add(token);
        out.push({ token, label: label || token, grupo: grupo || null });
      };
      (this.eventFieldsSchema || []).forEach(f => {
        if (f && f.name) push(':' + f.name, f.label || f.name, f.grupo);
      });
      // Fallback: se não há schema raw, usa eventFields (já normalizado).
      if (!this.eventFieldsSchema || !this.eventFieldsSchema.length) {
        (this.eventFields || []).forEach(f => {
          if (f && f.name) push(':' + f.name, f.label || f.name, f.grupo);
        });
      }
      // relations_schema (futuro C7) — ainda pode não vir.
      (this.eventRelationsSchema || []).forEach(r => {
        if (!r) return;
        const tk = r.token || (r.name ? ':' + r.name : null);
        if (tk) push(tk, r.label || r.name || tk, r.grupo || 'Relações');
      });
      return out;
    },
    // Mapeia tokens para a forma esperada pelo AcaoObjetoCampoSelect — assim
    // reaproveitamos o mesmo dropdown searchable usado em "Campos" das regras
    // visuais. O `name` recebe o token completo (ex.: ":pedidoId") para que a
    // detecção de seleção via @input funcione com o valor emitido.
    // Identifica a primeira ocorrência (ação + campo) onde o picker de tokens
    // será renderizado, para exibir a explicação introdutória somente uma vez.
    primeiroTokenFieldKey() {
      const acoes = this.acoesList || [];
      for (let idx = 0; idx < acoes.length; idx++) {
        const slug = acoes[idx].acao_tipo_slug;
        if (!slug) continue;
        const campos = this.schemaFor(slug) || [];
        for (const campo of campos) {
          if (this.isHandledByCustomBlock(slug, campo.name)) continue;
          if (this.shouldShowTokensFor(campo)) {
            return this.tokenKey(idx, campo.name);
          }
        }
      }
      return null;
    },
    availableTokensAsFields() {
      return (this.availableTokens || []).map(tk => ({
        name:  tk.token,
        label: tk.label,
        grupo: tk.grupo || null,
      }));
    },
    // Tokens que resolvem para colaborador — lidos do relations_schema (preferência)
    // ou inferidos pelo fields_schema.
    colaboradorTokens() {
      const out = [];
      const seen = new Set();
      const push = (token, label, grupo) => {
        if (!token || seen.has(token)) return;
        seen.add(token);
        out.push({ token, label: label || token, grupo: grupo || null });
      };
      (this.eventRelationsSchema || []).forEach(r => {
        if (!r) return;
        const target = String(r.target_entity || r.entidade || r.tipo || '').toLowerCase();
        if (target !== 'colaborador') return;
        // Mesma restrição de fieldLooksLikeColaborador: somente o colaborador
        // "dono" do registro — papéis adjacentes (vendedor, atendente...)
        // não são o destinatário natural do evento. Comparamos pelo último
        // segmento do path/name para suportar tanto `colaborador` quanto
        // `pedido.colaborador`.
        const ref = String(r.path || r.name || '').toLowerCase();
        const tail = ref.split('.').pop();
        if (tail !== 'colaborador' && tail !== 'colaborador_id' && tail !== 'colaboradorid') return;
        const tk = r.token || (r.name ? ':' + r.name : null);
        if (tk) push(tk, r.label || r.name || tk, r.grupo || 'Relações');
      });
      (this.eventFieldsSchema || []).forEach(f => {
        if (fieldLooksLikeColaborador(f)) {
          push(':' + f.name, f.label || f.name, f.grupo);
        }
      });
      return out;
    },
  },
  watch: {
    tipos: { immediate: true, handler() { this.preloadSources(); } },
    acoes: { immediate: true, handler() { this.normalizeLegacyDestinatarios(); } },
  },
  methods: {
    agruparTokensPorModel(tokens) {
      if (!tokens || !tokens.length) return [];
      const grupos = {};
      tokens.forEach(tk => {
        const nome = tk.grupo || 'Outros';
        if (!grupos[nome]) grupos[nome] = [];
        grupos[nome].push(tk);
      });
      const ordem = Object.keys(grupos).sort((a, b) => {
        if (a === 'Campos do Evento') return -1;
        if (b === 'Campos do Evento') return 1;
        if (a === 'Campos Adicionais' || a === 'Outros') return 1;
        if (b === 'Campos Adicionais' || b === 'Outros') return -1;
        return a.localeCompare(b);
      });
      return ordem.map(nome => ({ nome, tokens: grupos[nome] }));
    },
    schemaFor(slug) {
      const tipo = this.tipos.find(t => t.slug === slug);
      return tipo ? (tipo.config_schema || []) : [];
    },
    tipoNome(slug) {
      const tipo = this.tipos.find(t => t.slug === slug);
      return tipo ? tipo.nome : null;
    },
    preloadSources() {
      const sources = new Set();
      (this.tipos || []).forEach(t => {
        (t.config_schema || []).forEach(f => {
          if (f.type === 'select' && f.source) sources.add(f.source);
        });
      });
      sources.forEach(src => {
        if (this.sourceOptions[src]) return;
        get(src)
          .then(json => {
            this.$set(this.sourceOptions, src, Array.isArray(json) ? json : (json.data || []));
          })
          .catch(() => { this.$set(this.sourceOptions, src, []); });
      });
    },
    getCfg(acao, name) {
      return acao && acao.acao_config ? acao.acao_config[name] : null;
    },
    cloneAcoes() {
      return this.acoesList.map(a => ({
        ...a,
        acao_config: a.acao_config && typeof a.acao_config === 'object' ? { ...a.acao_config } : {},
      }));
    },
    emit(arr) { this.$emit('update:acoes', arr); },
    adicionar() {
      const arr = this.cloneAcoes();
      arr.push({ id: null, acao_tipo_slug: null, acao_config: {}, tipo_execucao: null, acao: null, on_error: 'continue', ordem: arr.length });
      this.emit(arr);
    },
    setOnError(idx, val) {
      const arr = this.cloneAcoes();
      arr[idx].on_error = val;
      this.emit(arr);
    },
    onDragEnd() {
      const arr = this.acoesList.map((a, i) => ({ ...a, ordem: i }));
      this.emit(arr);
    },
    remover(idx) {
      const arr = this.cloneAcoes();
      arr.splice(idx, 1);
      this.emit(arr);
    },
    setTipo(idx, slug) {
      const arr = this.cloneAcoes();
      arr[idx].acao_tipo_slug = slug || null;
      const cfg = {};
      this.schemaFor(slug).forEach(f => {
        cfg[f.name] = arr[idx].acao_config[f.name] !== undefined
          ? arr[idx].acao_config[f.name]
          : (f.default !== undefined ? f.default : null);
      });
      // Defaults específicos para 'notification'
      if (slug === 'notification') {
        if (!cfg.tipo) cfg.tipo = 'TIPO_SIMPLES';
        if (cfg.destinatarios === undefined || cfg.destinatarios === null) {
          // Pré-popula com o token do colaborador "dono" do evento, se houver,
          // para que a ação inicie já no modo "Tomar do evento". Sem isso, o
          // destMode (puramente derivado dos destinatarios) cairia em 'manual'.
          const first = this.colaboradorTokens[0];
          cfg.destinatarios = first ? [first.token] : [];
        } else {
          cfg.destinatarios = this.normalizeDestinatariosValue(cfg.destinatarios);
        }
      }
      arr[idx].acao_config = cfg;
      this.emit(arr);
    },
    // ------- Notification helpers -------
    isHandledByCustomBlock(slug, fieldName) {
      if (slug !== 'notification') return false;
      return ['tipo', 'tipo_id', 'destinatarios', 'destinatario', 'destinatarios_ids'].includes(fieldName);
    },
    // Em ações 'notification', o campo `colaboradores_ids` (label "IDs de
    // colaboradores") só faz sentido quando o usuário optou por "Escolher
    // manualmente" — caso contrário o destinatário vem do token do evento.
    isFieldHiddenByDestMode(acao, fieldName) {
      if (!acao || acao.acao_tipo_slug !== 'notification') return false;
      if (fieldName !== 'colaboradores_ids') return false;
      return this.destMode(acao) === 'token';
    },
    normalizeDestinatariosValue(val) {
      if (Array.isArray(val)) return val;
      if (val === null || val === undefined || val === '') return [];
      // CSV legacy ("123,456" ou ":vendedorColaboradorId")
      if (typeof val === 'string') {
        return val.split(',').map(s => s.trim()).filter(Boolean).map(s => {
          if (s.startsWith(':')) return s;
          const n = Number(s);
          return Number.isFinite(n) ? n : s;
        });
      }
      return [val];
    },
    normalizeLegacyDestinatarios() {
      // Roda em load: se alguma ação 'notification' veio com destinatarios em CSV, converte.
      let mutated = false;
      const arr = this.cloneAcoes();
      arr.forEach(a => {
        if (a.acao_tipo_slug !== 'notification') return;
        const cur = a.acao_config && a.acao_config.destinatarios;
        if (typeof cur === 'string') {
          a.acao_config = { ...(a.acao_config || {}), destinatarios: this.normalizeDestinatariosValue(cur) };
          mutated = true;
        }
      });
      if (mutated) this.emit(arr);
    },
    // Modo derivado puramente do conteúdo de destinatarios — token quando há
    // pelo menos um valor :tokenname, manual caso contrário (inclusive lista
    // vazia). Sem fallbacks: a troca entre os toggles é determinada apenas
    // pelo que o `setDestMode` grava em destinatarios.
    destMode(acao) {
      const list = (acao.acao_config && acao.acao_config.destinatarios) || [];
      const arr = Array.isArray(list) ? list : this.normalizeDestinatariosValue(list);
      if (arr.length && typeof arr[0] === 'string' && arr[0].startsWith(':')) return 'token';
      return 'manual';
    },
    setDestMode(idx, mode) {
      const arr = this.cloneAcoes();
      const cfg = arr[idx].acao_config || {};
      if (mode === 'token') {
        const first = this.colaboradorTokens[0];
        cfg.destinatarios = first ? [first.token] : [];
      } else {
        cfg.destinatarios = [];
      }
      arr[idx].acao_config = { ...cfg };
      this.emit(arr);
    },
    // Quando o destinatário é "tomado do evento", existe apenas o colaborador
    // dono do registro — não há escolha. Devolvemos o token salvo (se já casa
    // com algum dos colaboradorTokens) ou o primeiro disponível como fallback.
    colaboradorTokenSelecionado(acao) {
      const tokens = this.colaboradorTokens || [];
      if (!tokens.length) return null;
      const list = this.normalizeDestinatariosValue((acao.acao_config || {}).destinatarios);
      const saved = typeof list[0] === 'string' && list[0].startsWith(':') ? list[0] : null;
      return tokens.find(t => t.token === saved) || tokens[0];
    },
    getDestManual(acao) {
      const list = this.normalizeDestinatariosValue((acao.acao_config || {}).destinatarios);
      return list.filter(v => !(typeof v === 'string' && v.startsWith(':')));
    },
    setDestManual(idx, ids) {
      const arr = this.cloneAcoes();
      const norm = Array.isArray(ids) ? ids : [];
      arr[idx].acao_config = { ...(arr[idx].acao_config || {}), destinatarios: norm };
      this.emit(arr);
    },
    // ------- Tokens (chips) -------
    shouldShowTokensFor(campo) {
      if (!campo || !campo.name) return false;
      const isTextish = !campo.type || campo.type === 'text' || campo.type === 'textarea' || campo.type === 'html';
      if (!isTextish) return false;
      return TOKEN_TARGET_FIELD_NAMES.includes(String(campo.name).toLowerCase());
    },
    onFieldFocus(ev) {
      this.lastFocusedInput = ev && ev.target ? ev.target : null;
    },
    tokenKey(idx, name) {
      return `${idx}__${name}`;
    },
    // CampoSelect emite 'input' a cada keystroke (busca) e também ao selecionar.
    // Só consideramos seleção quando o valor casa com um token conhecido.
    onTokenPickerInput(idx, name, value) {
      const key = this.tokenKey(idx, name);
      this.$set(this.tokenBusca, key, value || '');
      if (!value) return;
      const match = (this.availableTokens || []).find(tk => tk.token === value);
      if (!match) return;
      this.onTokenClick(value);
      this.$set(this.tokenBusca, key, '');
    },
    onTokenClick(token) {
      const el = this.lastFocusedInput;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && typeof el.selectionStart === 'number') {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const before = el.value.substring(0, start);
        const after = el.value.substring(end);
        const newVal = before + token + after;
        el.value = newVal;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        const pos = start + token.length;
        this.$nextTick(() => { try { el.focus(); el.setSelectionRange(pos, pos); } catch (e) {} });
        return;
      }
      // Fallback: copia para clipboard.
      try {
        if (navigator && navigator.clipboard) {
          navigator.clipboard.writeText(token);
          if (this.$toasted) this.$toasted.show(`Token ${token} copiado`, { duration: 1500 });
        }
      } catch (e) { /* noop */ }
    },
    setCfg(idx, name, value) {
      const arr = this.cloneAcoes();
      arr[idx].acao_config = { ...(arr[idx].acao_config || {}), [name]: value };
      this.emit(arr);
    },
    abrirModalCampos(idx, name, tipoFiltro = null) {
      this.activePathTarget = { idx, name, tipoFiltro };
      this.$modal.show(this.modalCamposNome);
    },
    onPayloadCampoSelect(valor) {
      if (!this.activePathTarget) return;
      const { idx, name, tipoFiltro } = this.activePathTarget;
      // Em campos de e-mail, anexa como :token à lista CSV existente para que o
      // usuário possa combinar literais + variáveis sem perder o já digitado.
      if (tipoFiltro === 'email') {
        const atual = String(this.getCfg(this.acoesList[idx], name) || '').trim();
        const token = ':' + valor;
        const novo  = atual ? `${atual}, ${token}` : token;
        this.setCfg(idx, name, novo);
      } else {
        this.setCfg(idx, name, valor);
      }
      this.activePathTarget = null;
    },
    fieldLooksLikeEmail(f) {
      if (!f) return false;
      const tipo = String(f.type || '').toLowerCase();
      if (tipo === 'email') return true;
      const nome = String(f.name || '').toLowerCase();
      const tail = nome.split('.').pop();
      return tail === 'email' || tail === 'emails' || tail.startsWith('email_') || tail.endsWith('_email');
    },
  },
};
</script>

<style scoped>
.af-section__header { margin-bottom: 14px; }
.af-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d2939;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.af-section__hint {
  margin: 2px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}

.af-card {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.15s ease;
}
.af-card:hover { border-color: #dddce9; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04); }

.af-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(to right, #f9fafb, #fff);
  border-bottom: 1px solid #f2f4f7;
}
.af-card__title {
  display: flex;
  align-items: center;
  gap: 12px;
}
.af-card__num {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #f2f2f8;
  color: #3a3e81;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.af-card__label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.af-card__type {
  font-size: 13px;
  font-weight: 600;
  color: #1d2939;
}

.af-card__body { padding: 14px 16px; }

.af-config__divider {
  position: relative;
  text-align: center;
  margin: 14px 0 12px;
  font-size: 10px;
  font-weight: 700;
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.af-config__divider::before,
.af-config__divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: calc(50% - 50px);
  height: 1px;
  background: #e4e7eb;
}
.af-config__divider::before { left: 0; }
.af-config__divider::after { right: 0; }

.af-fg { margin-bottom: 12px; }
.af-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.af-required { color: #d9534f; }
.af-input {
  border-radius: 6px !important;
  border: 1px solid #dddce9 !important;
  font-size: 13px !important;
  transition: all 0.15s ease;
}
.af-input:focus {
  border-color: #3a3e81 !important;
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1) !important;
}
.af-input--code {
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace !important;
  font-size: 12px !important;
  background: #fafbfc;
}
.af-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: #98a2b3;
  line-height: 1.4;
}

/* Checkbox custom */
.af-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  padding: 6px 0;
}
.af-checkbox input { display: none; }
.af-checkbox__box {
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: transparent;
  transition: all 0.15s ease;
  font-size: 10px;
}
.af-checkbox input:checked + .af-checkbox__box {
  background: #3a3e81;
  border-color: #3a3e81;
  color: #fff;
}
.af-checkbox__text {
  font-size: 13px;
  color: #344054;
}

.af-payload-input {
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.af-payload-input .af-input { flex: 1; }

.af-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  font-family: inherit;
  white-space: nowrap;
}
.af-btn--block { width: 100%; justify-content: center; }
.af-btn--primary {
  background: #3a3e81;
  color: #fff;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.2);
}
.af-btn--primary:hover {
  background: #2f326b;
  transform: translateY(-1px);
}
.af-btn--ghost {
  background: #fff;
  border-color: #dddce9;
  color: #3a3e81;
}
.af-btn--ghost:hover:not(:disabled) {
  background: #f2f2f8;
  border-color: #3a3e81;
}
.af-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.af-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 12px;
}
.af-icon-btn--danger { border-color: #fbd7d6; color: #d9534f; }
.af-icon-btn--danger:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

.af-card__header-right { display: flex; align-items: center; gap: 12px; }
.af-card__drag {
  background: transparent;
  border: none;
  color: #98a2b3;
  cursor: grab;
  padding: 4px;
  font-size: 14px;
}
.af-card__drag:active { cursor: grabbing; }
.af-card--ghost { opacity: 0.5; background: #f2f2f8; }

.af-on-error { display: flex; align-items: center; gap: 8px; }
.af-on-error__label { font-size: 10px; color: #98a2b3; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 600; }
.af-on-error__group { display: inline-flex; background: #fff; border: 1px solid #dddce9; border-radius: 6px; padding: 2px; }
.af-on-error__btn {
  background: transparent;
  border: none;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #667085;
  cursor: pointer;
  border-radius: 4px;
  font-family: inherit;
}
.af-on-error__btn.is-active {
  background: #3a3e81;
  color: #fff;
}

/* Empty */
.af-empty {
  text-align: center;
  padding: 36px 20px;
  background: #fafbfc;
  border: 1px dashed #dddce9;
  border-radius: 10px;
  margin-bottom: 14px;
}
.af-empty__icon {
  font-size: 30px;
  color: #d0d5dd;
  margin-bottom: 10px;
  opacity: 0.7;
}
.af-empty__title {
  font-size: 13px;
  font-weight: 600;
  color: #475467;
  margin: 6px 0 2px;
}
.af-empty__hint {
  font-size: 12px;
  color: #98a2b3;
  margin: 0;
}

.af-tokens { margin-top: 8px; }
.af-tokens__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  margin-bottom: 4px;
}
.af-tokens__desc {
  margin: 0 0 8px;
  font-size: 11px;
  color: #98a2b3;
  line-height: 1.5;
}
.af-tokens__desc code {
  background: #f2f2f8;
  border: 1px solid #dddce9;
  color: #3a3e81;
  border-radius: 4px;
  padding: 0 4px;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 10.5px;
}

.af-dest-toggle {
  display: inline-flex;
  background: #fff;
  border: 1px solid #dddce9;
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.af-dest-token {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f7f7fc;
  border: 1px solid #dddce9;
  border-radius: 8px;
}
.af-dest-token__icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #3a3e81;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}
.af-dest-token__body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.af-dest-token__label {
  font-size: 12.5px;
  font-weight: 600;
  color: #1d2939;
}
.af-dest-token__code {
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  color: #3a3e81;
  background: #fff;
  border: 1px solid #dddce9;
  border-radius: 4px;
  padding: 1px 6px;
  align-self: flex-start;
}
</style>
